import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Field, Flex, HStack, Input, Link, Text } from '@chakra-ui/react';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
    useGetRegCustomerQuery,
    useUploadRegCustomerFileMutation,
    useUploadRegCustomerReverseFileMutation,
} from '../../../../api';
import { alertManager } from '../../../../utils/alertManager';
import Button from '../../../../components/base/Button';

let validatePassportFieldFn: (() => boolean) | null = null;

export const validateRegistrationPassportField = (): boolean => {
    if (!validatePassportFieldFn) {
        return true;
    }
    return validatePassportFieldFn();
};

export const RegistrationPassportField: React.FC = () => {
    const { t } = useTranslation();

    const { data: regCustomer } = useGetRegCustomerQuery();
    const [uploadRegCustomerFile, { isLoading: isUploadingFront }] = useUploadRegCustomerFileMutation();
    const [uploadRegCustomerReverseFile, { isLoading: isUploadingReverse }] =
        useUploadRegCustomerReverseFileMutation();
    const [selectedPassportFileName, setSelectedPassportFileName] = useState('');
    const [selectedPassportReverseFileName, setSelectedPassportReverseFileName] = useState('');
    const [passportError, setPassportError] = useState<string | null>(null);
    const passportInputRef = useRef<HTMLInputElement | null>(null);
    const passportReverseInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        validatePassportFieldFn = () => {
            const hasPassportFile =
                !!regCustomer?.passport_file_url && !!regCustomer?.passport_reverse_file_url;
            setPassportError(hasPassportFile ? null : t.registration.form.requiredField);
            return hasPassportFile;
        };

        return () => {
            validatePassportFieldFn = null;
        };
    }, [regCustomer?.passport_file_url, regCustomer?.passport_reverse_file_url]);

    useEffect(() => {
        if (regCustomer?.passport_file_url && regCustomer?.passport_reverse_file_url) {
            setPassportError(null);
        }
    }, [regCustomer?.passport_file_url, regCustomer?.passport_reverse_file_url]);

    const handlePassportFileChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        async (event) => {
            const file = event.target.files?.[0];
            setSelectedPassportFileName(file?.name ?? '');
            if (!file) return;

            setPassportError(null);

            try {
                await uploadRegCustomerFile(file).unwrap();
            } catch {
                alertManager.error(t.registration.errors.serverError);
            } finally {
                event.target.value = '';
            }
        },
        [t.registration.errors.serverError, uploadRegCustomerFile],
    );

    const handlePassportReverseFileChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        async (event) => {
            const file = event.target.files?.[0];
            setSelectedPassportReverseFileName(file?.name ?? '');
            if (!file) return;

            try {
                await uploadRegCustomerReverseFile(file).unwrap();
            } catch {
                alertManager.error(t.registration.errors.serverError);
            } finally {
                event.target.value = '';
            }
        },
        [t.registration.errors.serverError, uploadRegCustomerReverseFile],
    );

    return (
        <Field.Root invalid={!!passportError}>
            <Field.Label fontSize="md" fontWeight="bold" color="gray.800">
                {t.registration.form.passportFileLabel}
            </Field.Label>
            {regCustomer?.passport_file_url && (
                <HStack wrap="wrap" align="center" gap={2} mt={1} mb={1}>
                    <Text fontSize="md" color="gray.600">
                        {t.registration.form.passportFrontSideLabel}:
                    </Text>
                    <Link
                        href={regCustomer.passport_file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize="md"
                        color="blue.600"
                        textDecoration="underline"
                    >
                        {selectedPassportFileName || t.registration.form.openUploadedFile}
                    </Link>
                </HStack>
            )}
            {regCustomer?.passport_reverse_file_url && (
                <HStack wrap="wrap" align="center" gap={2} mt={1} mb={1}>
                    <Text fontSize="md" color="gray.600">
                        {t.registration.form.passportReverseFileLabel}:
                    </Text>
                    <Link
                        href={regCustomer.passport_reverse_file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize="md"
                        color="blue.600"
                        textDecoration="underline"
                    >
                        {selectedPassportReverseFileName || t.registration.form.openUploadedFile}
                    </Link>
                </HStack>
            )}
            <Flex w="full" gap={2} flexWrap="nowrap" align="stretch">
                <Input
                    ref={passportInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    display="none"
                    onChange={handlePassportFileChange}
                />
                <Input
                    ref={passportReverseInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    display="none"
                    onChange={handlePassportReverseFileChange}
                />
                <Button
                    flex={1}
                    minW={0}
                    buttonType={
                        passportError && !regCustomer?.passport_file_url ? 'danger-light' : 'secondary'
                    }
                    size="sm"
                    fontSize="sm"
                    px={{ base: 2, sm: 3 }}
                    whiteSpace="normal"
                    textAlign="center"
                    lineHeight="short"
                    isLoading={isUploadingFront}
                    disabled={isUploadingReverse}
                    onClick={() => passportInputRef.current?.click()}
                >
                    {t.registration.form.uploadPassportFrontButton}
                </Button>
                <Button
                    flex={1}
                    minW={0}
                    buttonType={
                        passportError && !regCustomer?.passport_reverse_file_url
                            ? 'danger-light'
                            : 'secondary'
                    }
                    size="sm"
                    fontSize="sm"
                    px={{ base: 2, sm: 3 }}
                    whiteSpace="normal"
                    textAlign="center"
                    lineHeight="short"
                    isLoading={isUploadingReverse}
                    disabled={isUploadingFront}
                    onClick={() => passportReverseInputRef.current?.click()}
                >
                    {t.registration.form.uploadPassportReverseButton}
                </Button>
            </Flex>
            {passportError && (
                <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                    {passportError}
                </Field.ErrorText>
            )}
        </Field.Root>
    );
};
